import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { deleteTournamentUseCase, getAllTournamentsUseCase } from '@/src/core/dependencies';
import { Tournament } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { useTournamentFilters } from '@/src/presentation/hooks';
import { HomeScreen } from '@/src/presentation/screens/HomeScreen';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';

const PAGE_SIZE = 20;

export default function MyTournamentsContainer() {
  const router = useRouter();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const {
    searchQuery,
    setSearchQuery,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    sortBy,
    setSortBy,
    filteredTournaments,
    hasActiveFilters,
    clearFilters,
  } = useTournamentFilters(tournaments);

  useFocusEffect(
    useCallback(() => {
      if (user) loadTournaments();
    }, [user?.id])
  );

  const loadTournaments = async () => {
    try {
      setLoading(true);
      setPage(0);
      setHasMore(true);
      const data = await getAllTournamentsUseCase.execute(PAGE_SIZE, 0);
      setTournaments(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch (error) {
      if (error instanceof Error && error.message === 'User not authenticated') return;
      logger.error('MyTournaments', 'Failed to load tournaments', { error });
      platformAlert(t('common.error'), t('errors.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTournaments = async () => {
    if (loadingMore || !hasMore || !user) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const data = await getAllTournamentsUseCase.execute(PAGE_SIZE, nextPage * PAGE_SIZE);

      if (data.length > 0) {
        setTournaments((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(data.length === PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      logger.error('MyTournaments', 'Failed to load more tournaments', { error });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleTournamentPress = (tournamentId: string) => {
    router.push(`/tournament/${tournamentId}`);
  };

  const handleCreateTournament = () => {
    router.push('/create-tournament');
  };

  const handleMenuPress = (tournament: Tournament, event: any) => {
    event.stopPropagation();
    setSelectedTournament(tournament);
    setMenuVisible(true);
  };

  const handleEdit = () => {
    setMenuVisible(false);
    if (selectedTournament) {
      if (selectedTournament.status !== 'setup') {
        platformAlert(t('common.error'), t('alerts.cannotEditNotSetup'));
        return;
      }
      router.push(`/edit-tournament/${selectedTournament.id}`);
    }
  };

  const handleDelete = () => {
    setMenuVisible(false);
    if (!selectedTournament) return;

    platformAlert(
      t('tournaments.deleteTournament'),
      t('alerts.confirmDeleteTournament'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteTournamentUseCase.execute(selectedTournament.id);
              platformAlert(t('common.success'), t('tournaments.tournamentDeleted'));
              loadTournaments();
            } catch (error) {
              logger.error('MyTournaments', 'Failed to delete tournament', { error });
              platformAlert(t('common.error'), t('tournaments.failedToDelete'));
            }
          },
        },
      ]
    );
  };

  return (
    <HomeScreen
      filteredTournaments={filteredTournaments}
      loading={loading}
      loadingMore={loadingMore}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      filterType={filterType}
      setFilterType={setFilterType}
      sortBy={sortBy}
      setSortBy={setSortBy}
      hasActiveFilters={hasActiveFilters}
      clearFilters={clearFilters}
      filtersVisible={filtersVisible}
      setFiltersVisible={setFiltersVisible}
      menuVisible={menuVisible}
      setMenuVisible={setMenuVisible}
      handleTournamentPress={handleTournamentPress}
      handleCreateTournament={handleCreateTournament}
      handleMenuPress={handleMenuPress}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      onRefresh={loadTournaments}
      onLoadMore={loadMoreTournaments}
      colors={colors}
    />
  );
}
